import { useEffect, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { CORNER_DOCK_ID, inscrireAuTri, lireTri, ordreEffectif, souscrireAuTri } from './cornerDock';

/**
 * Barre d'actions flottantes du coin bas-droit.
 *
 * Chaque bouton flottant posait auparavant son propre `fixed bottom-… right-…`
 * avec son propre z-index : deux d'entre eux affichés en même temps se
 * recouvraient, et le résultat dépendait de la page. Ils sont désormais des
 * ENFANTS d'un même conteneur, qui tient seul la position, l'espacement et
 * l'empilement.
 *
 * `CornerDock` se monte UNE fois dans le shell ; chaque bouton s'y insère par
 * `CornerDockSlot`, où qu'il vive dans l'arbre.
 *
 * Le conteneur ne capte pas le pointeur — sinon sa colonne invisible bloquerait
 * les clics sur la page en dessous ; chaque emplacement le réactive pour lui.
 */
export function CornerDock() {
  return (
    <div
      id={CORNER_DOCK_ID}
      // `right` et `bottom` en style inline plutôt qu'en classes : les deux
      // valeurs viennent de variables CSS alimentées par ce qui occupe le coin —
      // les panneaux droits ouverts pour l'une, la pile de notifications pour
      // l'autre. Les transitions accompagnent leur glissement au lieu de faire
      // sauter les boutons.
      //
      // La remontée existe parce que les deux se disputaient le même coin : les
      // notifications s'affichaient PAR-DESSUS le composeur de l'assistant, et
      // les décaler à sa gauche (là où vit cette barre) les aurait posées sur
      // « Signaler ». Chacun publie donc sa mesure, l'autre s'écarte.
      style={{
        right: 'calc(1rem + var(--orbit-dock-inset, 0px))',
        bottom: 'calc(1rem + var(--orbit-notices-hauteur, 0px))',
        transition: 'right 300ms ease-in-out, bottom 200ms ease-in-out',
      }}
      className="pointer-events-none fixed z-[900] flex flex-col items-end gap-3"
    />
  );
}

/**
 * Place son contenu dans la barre du coin.
 *
 * `order` place l'emplacement. Omis, il faut un `name` : les emplacements sans
 * ordre se rangent alors entre eux par ORDRE ALPHABÉTIQUE, après tous les ordres
 * explicites. Sans ce repli ils partageraient le même ordre et se classeraient
 * selon leur ordre de MONTAGE, qui change avec la route.
 *
 * La translucidité au repos vient de la recette `.dock-slot` (index.css) : elle
 * porte sur l'emplacement, donc sur le bouton entier.
 *
 * `className` habille l'EMPLACEMENT, pas le bouton : une visibilité responsive
 * posée sur le bouton laisserait un emplacement vide, et donc un espacement
 * fantôme entre ses voisins.
 *
 * Rend `null` tant que le conteneur n'existe pas : un bouton n'a pas de position
 * de repli, il attend son hôte. Le shell le monte au même commit, donc l'attente
 * ne dure qu'un rendu — la nouvelle tentative couvre le cas d'un slot monté
 * pendant que le shell se met en place.
 */
export function CornerDockSlot({ order, name, className = '', children }) {
  const [host, setHost] = useState(null);
  const classement = useSyncExternalStore(souscrireAuTri, lireTri, lireTri);

  // Sans ordre déclaré, l'emplacement s'inscrit au classement alphabétique.
  useEffect(() => {
    if (order != null || !name) return undefined;
    return inscrireAuTri(name);
  }, [order, name]);

  useEffect(() => {
    let annule = false;
    const chercher = () => {
      if (annule) return;
      const el = document.getElementById(CORNER_DOCK_ID);
      if (el) setHost(el);
      else requestAnimationFrame(chercher);
    };
    chercher();
    return () => { annule = true; };
  }, []);

  if (!host) return null;
  return createPortal(
    <div className={`pointer-events-auto dock-slot ${className}`} style={{ order: ordreEffectif(order, name, classement) }}>
      {children}
    </div>,
    host,
  );
}
